import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { ProjectionProviderType } from "@app/projects/enumerators";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { ProviderHubContext } from "@app/projects/network-assessment/provider-hub/contexts/ProviderHubContext";
import { ProviderProfileActionEnum, ServicingAccessPoint } from "@app/projects/network-assessment/provider-hub/interfaces";
import { ProjectionPanelMode, ProjectionProviderDetails } from "@app/projects/projection-provider";
import projectionProviderDetailsReducer, { projectionProviderDetailsInitialState, ProjectionProviderIdentifier } from "@app/projects/projection-provider/projectionProviderDetailsReducer";
import { AccessPointForProjection } from "@app/projects/results/projection-provider";
import { TerminatePanelMode, WrappedTerminateProvidersPanel } from "@app/projects/servicing-provider/TerminateProvidersPanel";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { formatSpecialtyWithCode } from "@app/projects/network-assessment/provider-hub/utility";
import { FC, useCallback, useContext, useReducer } from "react";

interface ProviderHubProjectionProviderFlyoutProps {
  accessPoints: ServicingAccessPoint[];
  providerAction: ProviderProfileActionEnum;
  allAccessPoints: boolean;
  individualAccessPoint?: boolean;
  onSubmit?: () => void;
  excludedAccessPointsCount?: number;
  targetedAccessPointsCount?: number;
}

const ProviderHubProfileActionFlyout: FC<ProviderHubProjectionProviderFlyoutProps> = ({
  accessPoints,
  providerAction,
  allAccessPoints,
  onSubmit,
  individualAccessPoint,
  excludedAccessPointsCount = 0,
  targetedAccessPointsCount = 0
}) => {
  const featureFlagContext = useContext(FeatureFlagContext);
  const projectContext = useContext(CountyProjectContext);
  const providerHubContext = useContext(ProviderHubContext);

  const showNetworkIntegrityReason = (featureFlagContext.enabledFeatureFlags?.has(Features.ENABLE_NETWORK_INTEGRITY_IN_PROVIDER_HUB) ?? false) &&
    (projectContext.project?.showNetworkIntegrityDashboard ?? false);

  const [projectionProvidersState, projectionProvidersReducer] = useReducer(projectionProviderDetailsReducer, projectionProviderDetailsInitialState);

  const { selectedProvider } = providerHubContext;

  const getProviderActionTypeDisplayName = (type: ProviderProfileActionEnum) => {
    switch (type) {
      case ProviderProfileActionEnum.Target:
        return "Targeting";
      case ProviderProfileActionEnum.Exclude:
        return "Excluding";
      case ProviderProfileActionEnum.Terminate:
        return "Terminating";
      case ProviderProfileActionEnum.Unknown:
        return "Unknown";
    }
  };

  const getProjectionProviderType = (type: ProviderProfileActionEnum): ProjectionProviderType => {
    switch (type) {
      case ProviderProfileActionEnum.Target:
        return ProjectionProviderType.Target;
      case ProviderProfileActionEnum.Exclude:
        return ProjectionProviderType.ExcludeImpact;
      default:
        return ProjectionProviderType.Unknown;
    }
  }

  const accessPointsForAction: AccessPointForProjection[] = accessPoints.map(ap => ({
    npi: ap.npi,
    providerId: ap.providerId,
    providerNetworkType: ap.providerNetworkType,
    specialtyGroupCode: ap.specialtyGroupCode,
    tin: ap.tin
  }) as AccessPointForProjection);

  const getSingleAccessPointDetails = () => {
    const accessPoint = accessPoints[0];
    const details = [
      accessPoint.address,
      accessPoint.address2,
      accessPoint.city,
      `${accessPoint.state} ${accessPoint.zip}`,
      accessPoint.specialtyDescription ? formatSpecialtyWithCode({ code: accessPoint.specialtyGroupCode, name: accessPoint.specialtyDescription }) : undefined,
    ];
    return details.filter(detail => !!detail && detail.trim() !== "").join(", ");
  }

  const onProjectionProvidersCreated = useCallback(async (runProjection: boolean, hasUnprocessedProviders = true) => {
    if (runProjection) {
      await projectContext.processProviderChanges(false);
    } else {
      projectContext.setProjectHasUnprocessedProjectionProviders(hasUnprocessedProviders, false, getProjectionProviderType(providerAction));
    }

    onSubmit();
  }, [projectContext, onSubmit, providerAction]);

  const hasTargetedAccessPoints = targetedAccessPointsCount > 0;
  const hasExcludedAccessPoints = excludedAccessPointsCount > 0;

  const renderActionAlertOrInfoBox = () => {
    if (providerAction === ProviderProfileActionEnum.Target && hasExcludedAccessPoints) {
      return (
        <QESAlert type="warning" persist>
          <p className="demibold mt-2 mb-2">Some access points are currently excluded. Target actions will only apply to access points without an action status. To target excluded access points, remove the Exclusion.</p>
        </QESAlert>
      );
    }

    if (providerAction === ProviderProfileActionEnum.Exclude && hasTargetedAccessPoints) {
      return (
        <QESAlert type="warning" persist>
          <p className="demibold mt-2 mb-2">Some access points have been targeted. Applying the Exclusion will override the existing Target rule.</p>
        </QESAlert>
      );
    }

    return (
      <div className="provider-profile-target-flyout__info-box">
        {allAccessPoints ?
          <p>{selectedProvider.name ?? "---"}: All Specialties, All Locations</p> :
          <p>{getProviderActionTypeDisplayName(providerAction)} will apply to all applicable {providerAction === ProviderProfileActionEnum.Terminate ? 'network' : 'out-of-network'} access points</p>
        }
      </div>
    );
  };

  return (
    <div>
      <p>Please enter the necessary information to {providerAction.toLocaleLowerCase()} the available providers for your network.</p>

      {individualAccessPoint ?
        <>
          <QESAlert type={providerAction === ProviderProfileActionEnum.Terminate ? "warning" : "info"} persist>
            <p className="demibold mt-2 mb-2">You have chosen to {providerAction.toLocaleLowerCase()} a specific access point for this provider.</p>
          </QESAlert>
          <div className="provider-profile-target-flyout__info-box">
            <p>{selectedProvider.name ?? "---"}: {getSingleAccessPointDetails()}</p>
          </div>
        </>
        :
        renderActionAlertOrInfoBox()
      }

      {providerAction === ProviderProfileActionEnum.Terminate ? (
        <WrappedTerminateProvidersPanel
          projectId={projectContext.project.projectId}
          onSubmit={() => {
            projectContext.setProjectHasUnprocessedTerminations(true);
            onSubmit();
          }}
          crossSpecialtyAndNetwork={individualAccessPoint ? null : {
            allLocations: true,
            allProjects: false,
            allSpecialties: true,
            projectionBy: ProjectionProviderIdentifier.NPI,
          }}
          terminatePanelMode={TerminatePanelMode.Create}
          selectedProviders={accessPointsForAction}
          specialtyGroupCode={individualAccessPoint ? accessPointsForAction?.[0]?.specialtyGroupCode : undefined}
          hideAllLocationsOption
          hideAllSpecialtiesOption
          showAffiliationsInsights={false}
          showNetworkIntegrityReason={showNetworkIntegrityReason}
        />
      ) : (
        <ProjectionProviderDetails
          accessPoints={accessPointsForAction}
          crossSpecialtyAndNetworkType={individualAccessPoint ? null :{
            allLocations: true,
            allProjects: false,
            allSpecialties: true,
            projectionBy: ProjectionProviderIdentifier.NPI,
          }}
          showAffiliationsInsights={projectContext.project.showAffiliationsInsights}
          formState={projectionProvidersState}
          formDispatch={projectionProvidersReducer}
          hasProvidersWithoutNPI={selectedProvider.npi === undefined}
          networkAssessmentAnalysisType={projectContext.runConfiguration.networkAssessmentAnalysisType}
          projectId={projectContext.project.projectId}
          projectionPanelMode={ProjectionPanelMode.Create}
          projectionProviderType={getProjectionProviderType(providerAction)}
          providerNetworkType={selectedProvider.providerNetworkTypes[0]}
          specialtyGroupCode=""
          isZipProject={false}
          isRunning={projectContext.project?.isRunning}
          hideProviderInfoSection
          onProjectionProvidersCreated={onProjectionProvidersCreated}
          hideAllLocationsOption
          hideAllSpecialtiesOption
          hideAllProjectsOption={!individualAccessPoint}
          isRegulatorProject={!!projectContext.project?.networkTaxonomy?.regulatorAssignmentClientId}
          regulatorAssignmentClient={projectContext.project?.networkTaxonomy?.regulatorAssignmentClient ?? ""}
        />
      )}
    </div>
  );
}

export default ProviderHubProfileActionFlyout;